import {
  type CalendarDate,
  differenceInCalendarDays,
  parseCalendarDate,
} from '@shared/dates/calendarDate';

import type { QueueViewKey } from '../../../types/encounterWork';

import { HOME_QUEUES, type HomeQueueDefinition, type HomeQueueRow } from './homeQueues';

/**
 * Home's summary strip as data (GDN-4085): one tile per queue, always all
 * five, in `HOME_QUEUES`' priority order, and the queue whose table shows
 * below them.
 *
 * Tiles never move. What the reads decide is each tile's STATE, and the
 * counts decide it first: every page read returns the counts for all five
 * queues, so the first read to answer settles every tile at once, and the
 * reads that land after it fill in their tables and ages without changing
 * which tile says what. A key no read has carried yet (before the first
 * answer, or from an older server that does not send it) waits for its own
 * read, and a read that failed is never taken for an empty queue.
 *
 * The age is measured from the service date of the queue's first row. Home
 * reads every queue `service_date_asc`, the only order the server offers
 * besides the charge, so the first row IS the queue's oldest by service date
 * and by nothing else: its `days_silent` is not the longest silence in
 * Awaiting payer, nor its `primary_era_at` the oldest remittance in Bill
 * secondary. Those clocks need a server sort of their own before a tile can
 * name them.
 */

/** What one queue's reads have said so far. */
export interface HomeQueueRead {
  key: QueueViewKey;
  /**
   * The queue's count in the newest snapshot any read returned. Undefined
   * when no read has carried this key, which is not the same fact as zero.
   */
  count: number | undefined;
  /** The queue's own page read has not answered yet. */
  loading: boolean;
  /** The queue's own page read failed. */
  failed: boolean;
  /** The rows the queue's own page read returned, oldest service date first. */
  rows: readonly HomeQueueRow[];
}

export interface HomeQueueTile {
  queue: HomeQueueDefinition;
  /**
   * The number the tile prints. Undefined while no read has said it: no
   * count has arrived for the key and the queue's own read is still out or
   * has failed.
   */
  count: number | undefined;
  /**
   * No work: the muted "All clear" tile. Never a queue whose own read
   * failed, even when another read counted it at zero: the failure stays in
   * front, with its retry.
   */
  clear: boolean;
  /** Whole days since the oldest row's service date; null when no row says. */
  oldestDays: number | null;
  /** The queue's own page read has not answered yet. */
  loading: boolean;
  /** The queue's own page read failed. */
  failed: boolean;
  /** This queue's table is the one below the strip. */
  selected: boolean;
}

export interface HomeQueueStrip {
  /** Always five, in priority order. */
  tiles: HomeQueueTile[];
  /**
   * The queue whose table shows. Null only while the counts cannot yet say
   * which queue comes first with work, which is before any read has
   * answered.
   */
  selectedKey: QueueViewKey | null;
}

/** A queue with no read at all behaves as one whose read is still out. */
const unread = (key: QueueViewKey): HomeQueueRead => ({
  count: undefined,
  failed: false,
  key,
  loading: true,
  rows: [],
});

/** Whole days since the row's service date; null without one, or for one ahead of today. */
const daysSinceService = (row: HomeQueueRow | undefined, today: CalendarDate): number | null => {
  const serviceDate = row?.service_date ? parseCalendarDate(row.service_date) : null;
  if (!serviceDate) return null;
  const days = differenceInCalendarDays(today, serviceDate);
  return days >= 0 ? days : null;
};

/** The count when a read carried it; otherwise the rows the queue's own read answered with. */
const countOf = (read: HomeQueueRead): number | undefined => {
  if (read.count !== undefined) return read.count;
  return read.loading || read.failed ? undefined : read.rows.length;
};

const tileFor = (
  queue: HomeQueueDefinition,
  read: HomeQueueRead,
  today: CalendarDate,
): HomeQueueTile => {
  const count = countOf(read);
  return {
    clear: count === 0 && !read.failed,
    count,
    failed: read.failed,
    loading: read.loading,
    oldestDays: daysSinceService(read.rows[0], today),
    queue,
    selected: false,
  };
};

/** Nothing has said yet whether this queue has work. */
const isUndecided = (tile: HomeQueueTile): boolean => tile.count === undefined && tile.loading;

const hasWork = (tile: HomeQueueTile): boolean => (tile.count ?? 0) > 0;

/**
 * The highest-priority queue with work. Null while a queue ABOVE it is still
 * undecided, so the choice waits for that answer instead of jumping when it
 * lands. With no work anywhere, the first queue whose read failed, so the
 * failure and its retry are in front of the biller rather than behind a tile.
 */
export const defaultHomeQueue = (tiles: readonly HomeQueueTile[]): QueueViewKey | null => {
  for (const tile of tiles) {
    if (isUndecided(tile)) return null;
    if (hasWork(tile)) return tile.queue.key;
  }
  return tiles.find(tile => tile.failed)?.queue.key ?? null;
};

/**
 * The strip for the reads so far. `requestedKey` is the queue the URL names,
 * and it wins even when that queue is empty: a biller who comes back to a
 * queue they just cleared finds it cleared, not some other queue.
 */
export const buildHomeQueueStrip = (
  reads: readonly HomeQueueRead[],
  requestedKey: QueueViewKey | null,
  today: CalendarDate,
): HomeQueueStrip => {
  const tiles = HOME_QUEUES.map(queue =>
    tileFor(queue, reads.find(read => read.key === queue.key) ?? unread(queue.key), today),
  );
  const selectedKey = requestedKey ?? defaultHomeQueue(tiles);
  return {
    selectedKey,
    tiles: tiles.map(tile => ({ ...tile, selected: tile.queue.key === selectedKey })),
  };
};

/** The tile's age line: "oldest 1 day", "oldest 44 days". */
export const oldestLabel = (days: number): string =>
  `oldest ${days} ${days === 1 ? 'day' : 'days'}`;
